/**
 * MergeButton - deep-links from an admin-viewed Frontend entity page straight into
 * the Merge Console with that entity prefilled.
 *
 * Reads window.AdminPanelConfig.entity, set server-side by
 * Frontend::ApplicationController#register_admin_panel_entity (see
 * _contextual_panel_hook.html.erb). That entity is present only on pages whose
 * controller opted in — i.e. the four types Admin::Api::MergesController's merge
 * console supports. Renders nothing on any other page (a plain user profile with no
 * promo entity, a request page, etc.) or if the entity's type isn't one of the four,
 * so this can be dropped into any context panel unconditionally.
 *
 * entity.merge_role picks which pair of query params to fill AND the button's
 * label: a self-representing expert can never be a merge source (Admin::Merging::
 * Expert refuses it; the picker disables it too), so it prefills as the destination
 * and reads "Merge In" — the natural intent on a canonical person's page is "merge
 * duplicates into this one". Every other entity prefills as the source under the
 * plain "Merge" label. Absent/unrecognized merge_role defaults to source.
 */

import React from "react";

// Mirrors Admin::MergeConsolesController::SUPPORTED_TYPES /
// Admin::Api::MergesController#supported_types.
const MERGEABLE_TYPES = new Set([
  "Id::MediaOutlet",
  "Id::PromoEntity::Company",
  "Id::PromoEntity::Product",
  "Id::PromoEntity::Expert",
]);

function MergeButton() {
  const entity = window.AdminPanelConfig?.entity;
  if (!entity || !MERGEABLE_TYPES.has(entity.type) || entity.id == null) return null;

  const asDestination = entity.merge_role === "destination";
  const idKey = asDestination ? "destination_id" : "source_id";
  const labelKey = asDestination ? "destination_label" : "source_label";

  const params = new URLSearchParams({
    type: entity.type,
    [idKey]: String(entity.id),
    [labelKey]: entity.name || "",
  });

  return (
    <>
      <a
        href={`/admin/merge_console?${params}`}
        className="btn btn-sm btn-outline-garish"
        data-testid="merge-chip-button"
      >
        <i className="fa fa-object-group me-2" />
        {asDestination ? "Merge In" : "Merge"}
      </a>
      {asDestination && (
        <p className="small text-muted mb-0" data-testid="merge-chip-footnote">
          * Self-experts cannot be merged away
        </p>
      )}
    </>
  );
}

export default MergeButton;
